import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useMultiSourceWaitTime } from "@/hooks/useMultiSourceWaitTime";

interface LiveWaitStripProps {
  airportCode: string;
}

/**
 * Compact live security wait readout for secondary airport pages
 * (terminal + parking pages). Links back to the full airport page.
 */
export const LiveWaitStrip = ({ airportCode }: LiveWaitStripProps) => {
  const { waitTime, loading } = useMultiSourceWaitTime(airportCode.toUpperCase());

  const minutes = waitTime?.standard.waitTime;
  const tone =
    minutes === undefined
      ? "text-muted-foreground"
      : minutes < 10
        ? "text-green-600 dark:text-green-500"
        : minutes <= 20
          ? "text-amber-600 dark:text-amber-500"
          : "text-red-600 dark:text-red-500";

  return (
    <Card className="rounded-xl shadow-md border-primary/20 bg-primary/5">
      <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Clock className="h-6 w-6 text-primary shrink-0" aria-hidden="true" />
          <div>
            <p className="text-base font-semibold text-foreground">
              Security right now at {airportCode.toUpperCase()}
            </p>
            <p className={`text-base ${tone}`}>
              {loading
                ? "Checking the line…"
                : minutes !== undefined
                  ? `About ${minutes} minute${minutes === 1 ? "" : "s"} to clear security`
                  : "Live estimate available on the airport page"}
            </p>
          </div>
        </div>
        <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 shrink-0">
          <Link to={`/airports/${airportCode.toLowerCase()}`}>
            See live wait times
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};
